import React, {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import {FilterInput} from 'src/components/input';
import {NoResultPage} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {FlatButton} from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Typography from '@pb/kit-component-typography';

import {SECTION_NAME} from './constants';
import {useAuditLogFilters} from './hooks/useAuditLogFilters';
import {useGetMcpLimitLog} from './hooks/useGetMcpLimitLog';
import {McpLimitLogTable} from './mcp-limit-log-table';
import {mcpLimitLogDataActions} from './slices/mcp-limit-log-data.slice';
import {mcpLimitLogStateActions, mcpLimitLogStateSelector} from './slices/mcp-limit-log-state.slice';

import {
    mcpLimitAuditLogClearButton,
    mcpLimitAuditLogLoadingContainer,
    mcpLimitAuditLogSectionTitle,
    mcpLimitAuditLogSeparator,
    mcpLimitAuditLogTotalCount,
} from './mcp-limit-audit-log.module.styl';

import type {McpLimitType} from 'src/openapi/admin/models/McpLimitType';
import type {SortDirection} from 'src/openapi/admin/models/SortDirection';

type Props = {
    accountId: number,
};

export const McpLimitAuditLog = ({accountId}: Props) => {
    const dispatch = useDispatch();
    const {loadingError, result, cacheKeys} = useGetMcpLimitLog({accountId});
    const sortState = useSelector(mcpLimitLogStateSelector);
    const sortDirection: SortDirection = sortState.sortCriteria.direction || 'DESC';

    const {
        handleAccountEmailChange,
        handleAccountEmailClear,
        handleLimitTypesChange,
        handleDateFilterChange,
        clearAllFilters,
        filtersCount,
    } = useAuditLogFilters({
        accountId,
        filters: cacheKeys.filters,
    });

    const handleSort = (direction: 'ASC' | 'DESC') => {
        dispatch(
            mcpLimitLogStateActions.setSortCriteria({
                direction: direction as SortDirection,
                field: 'timestamp',
            }),
        );
    };

    const noResultMessage = useMemo(() => {
        const hasFilters =
            cacheKeys.filters.accountEmail ||
            cacheKeys.filters.limitTypes?.length ||
            cacheKeys.filters.startDate ||
            cacheKeys.filters.endDate;

        if (hasFilters && !result?.data.length) {
            return <NoResultPage title="No data found for the selected criteria." description=""/>;
        }
        if (!hasFilters) {
            return <NoResultPage title="No data to show." description=""/>;
        }
        return null;
    }, [cacheKeys.filters, result?.data.length]);

    useEffect(() => {
        return () => {
            dispatch(mcpLimitLogDataActions.clearData());
            dispatch(mcpLimitLogStateActions.clearData());
        };
    }, [dispatch]);

    if (loadingError) {
        return null;
    }

    return (
        <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
            <div>
                <header className={mcpLimitAuditLogSectionTitle}>
                    <Group direction="h" alignItems="center">
                        <Typography fontWeight="semi-bold" variant="heading-level-3">
                            {SECTION_NAME}
                        </Typography>
                        {filtersCount > 0 && (
                            <>
                                <span className={mcpLimitAuditLogSeparator}/>
                                <span className={mcpLimitAuditLogTotalCount}>
                                    {filtersCount} filter{filtersCount > 1 ? 's' : ''} active
                                </span>
                            </>
                        )}
                    </Group>
                </header>
                <Group direction="v" gap="s" as="section">
                    <FilterInput
                        id="mcp-limit-audit-log-filter"
                        placeholder="Search by user email or account name"
                        onChange={handleAccountEmailChange}
                        onClear={handleAccountEmailClear}
                        timeOut={1000}
                        width={600}
                        maxLength={64}
                        value={(cacheKeys.filters?.accountEmail || '') as string}
                    />
                    <FlatButton
                        className={mcpLimitAuditLogClearButton}
                        startIcon={<Icon type={Icons.TRASH}/>}
                        onClick={clearAllFilters}
                        disabled={filtersCount === 0}>
                        Clear all criteria
                    </FlatButton>
                </Group>
            </div>
            <BoxLoading loading={!result}>
                {result?.data.length ? (
                    <div className={mcpLimitAuditLogLoadingContainer}>
                        <McpLimitLogTable
                            rows={result?.data || []}
                            totalCount={result?.totalCount || 0}
                            limitTypes={cacheKeys.filters.limitTypes as McpLimitType[] | []}
                            startDate={cacheKeys.filters.startDate as string | undefined}
                            endDate={cacheKeys.filters.endDate as string | undefined}
                            onLimitTypesChange={handleLimitTypesChange}
                            onDateFilterChange={handleDateFilterChange}
                            onSort={handleSort}
                            sortDirection={sortDirection}
                        />
                    </div>
                ) : (
                    noResultMessage
                )}
            </BoxLoading>
        </ErrorBoundary>
    );
};
